import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { FaArrowRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

const BlogPost = React.memo(({ post, index, readArticleText, blogImages }) => {
  const blogPostImagesArray = useMemo(
    () => [blogImages.post1, blogImages.post2, blogImages.post3],
    [blogImages]
  );

  return (
    <motion.div
      key={index}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group"
    >
      {/* <Link
        to={`/blog/${post.slug || index}`}
        aria-label={`Read article: ${post.title}`}
        className="block"
      > */}
      <div className="relative overflow-hidden rounded-2xl bg-gray-800">
        <div className="aspect-16/9 relative">
          <img
            src={blogPostImagesArray[index] || blogPostImagesArray[0]}
            alt={post.title}
            className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-linear-to-b from-transparent via-black/40 to-black/80" />
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <div className="flex items-center gap-3 text-sm mb-3">
            <span className="text-sm bg-gray-700 px-3 py-1 rounded-full backdrop-blur-xs">
              {post.category}
            </span>
            <span className="text-gray-400 text-sm">{post.date}</span>
          </div>

          <h2 className="text-md md:text-xl font-semibold mb-4 group-hover:text-blue-400 transition-colors">
            {post.title}
          </h2>

          <div className="flex items-center gap-2 text-sm text-gray-400 group-hover:text-blue-400 transition-colors">
            <span>{readArticleText}</span>
            <FaArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </div>
        </div>
      </div>
      {/* </Link> */}
    </motion.div>
  );
});

BlogPost.displayName = "BlogPost";

BlogPost.propTypes = {
  post: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    slug: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  readArticleText: PropTypes.string.isRequired,
  blogImages: PropTypes.shape({
    post1: PropTypes.string.isRequired,
    post2: PropTypes.string.isRequired,
    post3: PropTypes.string.isRequired,
  }).isRequired,
};

function BlogGrid({ blogImages }) {
  const { t } = useTranslation();
  const blogGrid = t("components.blog.blogGrid", { returnObjects: true });
  const { posts } = blogGrid;

  const memoizedPosts = useMemo(() => posts, [posts]);

  return (
    <div className="bg-black overflow-hidden" id="safePad">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-7xl mx-auto px-4 py-12">
        {memoizedPosts.map((post, index) => (
          <BlogPost
            key={post.id || index}
            post={post}
            index={index}
            readArticleText={blogGrid.readArticle}
            blogImages={blogImages}
          />
        ))}
      </div>
    </div>
  );
}

BlogGrid.propTypes = {
  blogImages: PropTypes.shape({
    post1: PropTypes.string.isRequired,
    post2: PropTypes.string.isRequired,
    post3: PropTypes.string.isRequired,
  }).isRequired,
};

export default React.memo(BlogGrid);
